import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography, Button, Stack } from '@mui/material';
import { motion, useInView } from 'framer-motion';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../hooks/useAnalytics';
import config from '../config';
import WaitlistBanner from './WaitlistBanner';
import WhatsAppRequestModal from './modals/WhatsAppRequestModal';
import '../styles/MainLanding.css';

// FAQ Item Component with collapsible functionality
const FAQItem = ({ question, answer }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <div className="faq-item">
      <div 
        className="faq-question"
        onClick={() => setIsOpen(!isOpen)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            setIsOpen(!isOpen);
          }
        }}
      >
        <Typography variant="h6" className="question-text">
          {question}
        </Typography>
        <span className={`faq-arrow ${isOpen ? 'open' : ''}`}>
          ▼
        </span>
      </div>
      <div className={`faq-answer ${isOpen ? 'open' : ''}`}>
        <div className="answer-text">
          {typeof answer === 'string' ? (
            <Typography variant="body1" className="answer-text">
              {answer}
            </Typography>
          ) : (
            answer
          )}
        </div>
      </div>
    </div>
  );
};

// Animated Section Component for smooth scroll animations
const AnimatedSection = ({ children, className = "", delay = 0, ...props }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { 
    once: true, 
    amount: 0.2,
    margin: "-50px 0px -50px 0px"
  });

  return (
    <motion.div
      ref={ref}
      className={className}
      initial={{ opacity: 0, y: 60 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 60 }}
      transition={{ 
        duration: 0.8, 
        delay: delay,
        ease: [0.25, 0.46, 0.45, 0.94]
      }}
      {...props}
    >
      {children}
    </motion.div>
  );
};

const MainLanding = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, isAuthenticated } = useAuth();
  const { trackButtonClick, trackUserAction } = useAnalytics();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const [currentAppScreenshot, setCurrentAppScreenshot] = useState(0);
  const autoRotateRef = useRef(null);
  const appAutoRotateRef = useRef(null);
  const [featuresDropdownOpen, setFeaturesDropdownOpen] = useState(false);
  const [isPaused, setIsPaused] = useState(false); // Track if auto-rotation is paused
  const [isTransitioning, setIsTransitioning] = useState(false); // Track header animation state
  const [isAppPaused, setIsAppPaused] = useState(false); // Track if app section auto-rotation is paused
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [expandedImage, setExpandedImage] = useState(null); // State for expanded image modal
  const [expandedImageIndex, setExpandedImageIndex] = useState(0); // Track current image index in modal
  
  // Waitlist gift modal state
  const [showUrgencyModal, setShowUrgencyModal] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  
  // Waitlist gift banner state
  const [showBanner, setShowBanner] = useState(false);
  const [hasShownBanner, setHasShownBanner] = useState(false);
  
  // WhatsApp request modal state
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  
  // Founder Deal snackbar state
  const [showFounderDeal, setShowFounderDeal] = useState(false);
  
  // Check if this is the waitlist gift URL (hash-based routing)
  const isWaitlistGiftURL = location.pathname === '/waitlist-gift' || 
                           window.location.hash === '#/waitlist-gift' ||
                           location.hash === '#/waitlist-gift';

  // Automatically logout any authenticated user when they visit the landing page
  // BUT NOT if they're coming from a direct link that requires authentication
  useEffect(() => {
    if (isAuthenticated) {
      // Check if user is accessing a direct link that needs authentication
      const currentPath = window.location.hash.replace('#', '') || window.location.pathname;
      const isDirectLink = currentPath.includes('/group/') || 
                          currentPath.includes('/events/') || 
                          currentPath.includes('/classes/') ||
                          currentPath.includes('/membership') ||
                          currentPath.includes('/onboarding') ||
                          currentPath.includes('/claim/') ||
                          currentPath.includes('/names/');
      
      if (isDirectLink) {
        console.log('🔄 Auto-logout: User is authenticated but accessing direct link, skipping auto-logout');
        console.log('🔄 Auto-logout: Current path:', currentPath);
        return;
      }
      
      // Check if user just completed authentication through DirectLinkAuth
      const isDirectLinkAuth = localStorage.getItem('directLinkAuthSuccess') === 'true';
      if (isDirectLinkAuth) {
        console.log('🔄 Auto-logout: User just authenticated via DirectLinkAuth, skipping auto-logout');
        return;
      }
      
      console.log('🔄 Auto-logout: User is authenticated, logging out to allow fresh start');
      logout();
    }
  }, [isAuthenticated, logout]);

  // Waitlist gift banner effect
  useEffect(() => {
    if (!isWaitlistGiftURL || hasShownBanner) return;

    // Show banner immediately
    setShowBanner(true);
    setHasShownBanner(true);
  }, [isWaitlistGiftURL, hasShownBanner]);

  const screenshots = [
    {
      type: 'image',
      src: 'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/Eventfeed.png'
    },
    {
      type: 'video',
      src: '/assets/mainvideo.mp4'
    }
  ];

  const appScreenshots = [
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/Frame%2028%20(3).png',
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/Frame%2028%20(6).png',
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/Frame%2028%20(5).png',
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/Frame%2028%20(4).png',
    'https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/Frame%2028%20(1).png'
  ];

  const headers = [
    "Your Community. One App. Zero Complexity.",
    "Keep Everyone Connected — Instantly.",
    "RSVPs Made Effortless.", 
    "Prayer Times, Simplified.",
    "Donations Made Simple."
  ];

  const subheaders = [
    "Events, classes, announcements, pledges, and donations — all in one seamless space members actually enjoy using.",
    "Your community board keeps members engaged and informed with announcements, prayer requests, newsletters, updates, and Torah inspiration — all in one place.",
    "Members can see events, details, and RSVP in one tap — no more long forms, lost emails, or confusion. Everything stays organized, simple, and clear.",
    "Yesod keeps everyone in sync with up-to-date prayer times and schedules — with real-time zmanim and friendly notification reminders.",
    "Members can donate to Aliyot, programs, Kiddush, and more—fully customizable by your shul. Seamless for your community, simple for your admin."
  ];

  // Function to render header with highlighted "Your" for the first header
  const renderHeader = (headerText, index) => {
    if (index === 0) {
      // For the first header, highlight "Your" word
      return (
        <>
          <span className="highlight-your">Your</span> Community. One App. Zero Complexity.
        </>
      );
    }
    return headerText;
  };

  const handleTryForFree = () => {
    trackButtonClick('Sign Up', 'landing_page', {
      section: 'hero'
    });
    
    // Check if user came from waitlist-gift route and pass that information
    if (isWaitlistGiftURL) {
      navigate('/signup?from=waitlist-gift', { state: { fromWaitlistGift: true } });
    } else {
      navigate('/signup');
    }
  };

  const handleSeePreview = () => {
    trackButtonClick('See a Preview', 'landing_page', {
      section: 'content'
    });
    navigate('/community-onboarding');
  };
  
  const handleLogin = () => {
    trackButtonClick('Login', 'landing_page', {
      section: 'hero'
    });
    navigate('/login');
  };

  // Navigation functions for menu
  const scrollToSection = (sectionId) => {
    trackUserAction('section_navigation', {
      target_section: sectionId,
      location: 'landing_page'
    });
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
    setFeaturesDropdownOpen(false);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (featuresDropdownOpen && !event.target.closest('.dropdown-container')) {
        setFeaturesDropdownOpen(false);
      }
    };

    if (featuresDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [featuresDropdownOpen]);

  // Keyboard event handler for closing modal with Escape key and navigation with arrow keys
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!expandedImage) return;
      
      if (event.key === 'Escape') {
        setExpandedImage(null);
      } else if (event.key === 'ArrowLeft') {
        event.preventDefault();
        const newIndex = expandedImageIndex > 0 ? expandedImageIndex - 1 : appScreenshots.length - 1;
        setExpandedImageIndex(newIndex);
        setExpandedImage(appScreenshots[newIndex]);
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        const newIndex = expandedImageIndex < appScreenshots.length - 1 ? expandedImageIndex + 1 : 0;
        setExpandedImageIndex(newIndex);
        setExpandedImage(appScreenshots[newIndex]);
      }
    };

    if (expandedImage) {
      document.addEventListener('keydown', handleKeyDown);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    } else {
      // Restore body scroll when modal is closed
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'auto';
    };
  }, [expandedImage, expandedImageIndex, appScreenshots]);

  const handleFeaturesClick = () => {
    trackButtonClick('Features Dropdown', 'landing_page', {
      action: featuresDropdownOpen ? 'close' : 'open'
    });
    setFeaturesDropdownOpen(!featuresDropdownOpen);
  };

  const handleYesodAppClick = () => {
    trackButtonClick('Yesod App', 'landing_page', {
      section: 'navigation'
    });
    scrollToSection('yesod-app-section');
  };

  const handleFAQClick = () => {
    trackButtonClick('FAQ', 'landing_page', {
      section: 'navigation'
    });
    scrollToSection('faq-section');
  };

  const handleBookDemoClick = () => {
    trackButtonClick('Book a Demo', 'landing_page', {
      section: 'navigation'
    });
    scrollToSection('demo-section');
  };

  const handleGetHelpClick = () => {
    trackButtonClick('How Does Yesod Work?', 'landing_page', {
      section: 'navigation'
    });
    navigate('/community-onboarding?modal=how-it-works');
  };

  // Navigation functions for expanded image modal
  const handlePrevImage = (e) => {
    e.stopPropagation();
    const newIndex = expandedImageIndex > 0 ? expandedImageIndex - 1 : appScreenshots.length - 1;
    setExpandedImageIndex(newIndex);
    setExpandedImage(appScreenshots[newIndex]);
  };

  const handleNextImage = (e) => {
    e.stopPropagation();
    const newIndex = expandedImageIndex < appScreenshots.length - 1 ? expandedImageIndex + 1 : 0;
    setExpandedImageIndex(newIndex);
    setExpandedImage(appScreenshots[newIndex]);
  };

  // Touch handlers for modal swipe navigation
  const [modalTouchStart, setModalTouchStart] = useState(null);
  const [modalTouchEnd, setModalTouchEnd] = useState(null);

  const handleModalTouchStart = (e) => {
    setModalTouchEnd(null);
    setModalTouchStart(e.targetTouches[0].clientX);
  };

  const handleModalTouchMove = (e) => {
    setModalTouchEnd(e.targetTouches[0].clientX);
  };

  const handleModalTouchEnd = () => {
    if (!modalTouchStart || !modalTouchEnd) return;
    
    const distance = modalTouchStart - modalTouchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNextImage({ stopPropagation: () => {} });
    } else if (isRightSwipe) {
      handlePrevImage({ stopPropagation: () => {} });
    }
  };

  // Auto-rotate images every 8 seconds (unless paused) - DISABLED
  useEffect(() => {
    // Auto-rotation is disabled
    return () => {
      if (autoRotateRef.current) {
        clearTimeout(autoRotateRef.current);
      }
    };
  }, [headers.length, isPaused]);

  // Auto-rotate app screenshots every 8 seconds
  useEffect(() => {
    if (isAppPaused) return; // Don't auto-rotate if paused
    
    const getRandomInterval = () => 8000; // 8 seconds
    
    const scheduleNext = () => {
      return setTimeout(() => {
        setCurrentAppScreenshot((prev) => (prev + 1) % appScreenshots.length);
        appAutoRotateRef.current = scheduleNext(); // Schedule the next rotation
      }, getRandomInterval());
    };

    appAutoRotateRef.current = scheduleNext();

    return () => {
      if (appAutoRotateRef.current) {
        clearTimeout(appAutoRotateRef.current);
      }
    };
  }, [appScreenshots.length, isAppPaused]);

  // Handle window resize for responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load Calendly widget script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      // Cleanup script when component unmounts
      const existingScript = document.querySelector('script[src="https://assets.calendly.com/assets/external/widget.js"]');
      if (existingScript) {
        document.body.removeChild(existingScript);
      }
    };
  }, []);

  // Show Founder Deal snackbar after 8 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowFounderDeal(true);
    }, 8000); // 8 seconds

    return () => clearTimeout(timer);
  }, []);

  // Handle URL hash navigation to scroll to sections
  useEffect(() => {
    // Check for section query parameter (e.g., ?section=demo-section)
    const searchParams = new URLSearchParams(location.search);
    const sectionId = searchParams.get('section');
    
    if (sectionId) {
      // Wait for page to fully load before scrolling
      const timer = setTimeout(() => {
        const element = document.getElementById(sectionId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [location]);

  const handleNext = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev + 1) % headers.length);
      setIsTransitioning(false);
    }, 150);
    setIsPaused(true); // Pause auto-rotation when user manually navigates
  };

  const handlePrev = () => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex((prev) => (prev - 1 + headers.length) % headers.length);
      setIsTransitioning(false);
    }, 150);
    setIsPaused(true); // Pause auto-rotation when user manually navigates
  };

  const handleDotClick = (index) => {
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentIndex(index);
      setIsTransitioning(false);
    }, 150);
    setIsPaused(true); // Pause auto-rotation when user manually navigates
  };

  const handleCarouselMouseEnter = () => {
    setIsPaused(true); // Pause auto-rotation on hover
  };

  const handleCarouselMouseLeave = () => {
    // Resume auto-rotation after a delay when mouse leaves (optional)
    setTimeout(() => {
      setIsPaused(false);
    }, 2000); // Resume after 2 seconds
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const minSwipeDistance = 50;

    if (Math.abs(distance) > minSwipeDistance) {
      setIsTransitioning(true);
      setTimeout(() => {
        if (distance > 0) {
          setCurrentIndex((prev) => (prev + 1) % headers.length);
        } else {
          setCurrentIndex((prev) => (prev - 1 + headers.length) % headers.length);
        }
        setIsTransitioning(false);
      }, 150);
      setIsPaused(true); // Pause auto-rotation when user swipes
    }
  };

  return (
    <div className="main-landing">
      {/* Waitlist Gift Banner */}
      <WaitlistBanner 
        isVisible={showBanner} 
      />
      
      {/* Header with logo, navigation, and buttons */}
        <header className="w-full py-4 mb-12">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Logo */}
            <div className="flex justify-center lg:justify-start">
              <img 
                src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/App%20Landing%20Page/Yesod%20Logo%204K.png" 
                alt="Yesod Logo" 
                className="h-14 lg:h-16 w-auto"
              />
            </div>
            
            {/* Navigation Menu - Hidden on mobile, shown on desktop */}
            <nav className="hidden lg:flex items-center gap-6">
              <div className="relative dropdown-container">
                <button 
                  className={`flex items-center gap-2 px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-all ${featuresDropdownOpen ? 'text-blue-600 bg-blue-50' : ''}`}
                  onClick={handleFeaturesClick}
                >
                  <span className="font-medium">Features</span>
                  <span className={`text-xs transition-transform duration-200 ${featuresDropdownOpen ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                {featuresDropdownOpen && (
                  <div className="absolute top-full left-0 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl min-w-[160px] z-50 py-2">
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('pledges-section')}
                    >
                      Pledges
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('classes-section')}
                    >
                      Classes
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('prayer-section')}
                    >
                      Prayer Schedule
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('notifications-section')}
                    >
                      Notifications
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('donations-section')}
                    >
                      Donations
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('events-section')}
                    >
                      Events
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('calendar-section')}
                    >
                      Community Calendar
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('posts-section')}
                    >
                      Posts
                    </button>
                  </div>
                )}
              </div>
              <button 
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all"
                onClick={() => scrollToSection('ai-assistant-section')}
              >
                AI Assistant
              </button>
              <button 
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all"
                onClick={() => {
                  navigate('/pricing');
                  window.scrollTo(0, 0);
                }}
              >
                Pricing
              </button>
              <button 
                className="px-4 py-2 text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all"
                onClick={handleFAQClick}
              >
                FAQ
              </button>
              {config.FEATURE_FLAGS?.WHATSAPP_SECTION && (
                <button 
                  className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                  onClick={() => {
                    setShowWhatsAppModal(true);
                    trackButtonClick('WhatsApp Icon', 'navbar', {
                      section: 'navigation'
                    });
                  }}
                  title="WhatsApp Updates"
                >
                  <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </button>
              )}
              <button 
                className="px-3 py-2 bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-all text-sm"
                onClick={handleBookDemoClick}
              >
                Book a Demo
              </button>
            </nav>

            {/* Mobile Navigation Menu */}
            <nav className="flex lg:hidden flex-wrap items-center justify-center gap-2 px-2">
              <div className="relative dropdown-container">
                <button 
                  className={`flex items-center gap-1 px-3 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg transition-all ${featuresDropdownOpen ? 'text-blue-600 bg-blue-50' : ''}`}
                  onClick={handleFeaturesClick}
                >
                  <span className="font-medium">Features</span>
                  <span className={`text-xs transition-transform duration-200 ${featuresDropdownOpen ? 'rotate-180' : ''}`}>
                    ▼
                  </span>
                </button>
                {featuresDropdownOpen && (
                  <div className="fixed left-1/2 -translate-x-1/2 mt-2 bg-white border border-gray-200 rounded-lg shadow-xl w-[90%] max-w-[300px] z-50 py-2">
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('classes-section')}
                    >
                      Classes
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('prayer-section')}
                    >
                      Prayer Schedule
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('notifications-section')}
                    >
                      Notifications
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('donations-section')}
                    >
                      Donations
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('events-section')}
                    >
                      Events
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('calendar-section')}
                    >
                      Community Calendar
                    </button>
                    <button 
                      className="w-full text-left px-4 py-3 text-gray-700 hover:bg-blue-50 hover:text-blue-600 transition-colors text-sm"
                      onClick={() => scrollToSection('posts-section')}
                    >
                      Posts
                    </button>
                  </div>
                )}
              </div>
              <button 
                className="px-3 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all"
                onClick={() => scrollToSection('ai-assistant-section')}
              >
                AI Assistant
              </button>
              <button 
                className="px-3 py-2 text-sm text-gray-700 hover:text-blue-600 hover:bg-gray-50 rounded-lg font-medium transition-all"
                onClick={handleFAQClick}
              >
                FAQ
              </button>
              {config.FEATURE_FLAGS?.WHATSAPP_SECTION && (
                <button 
                  className="p-2 text-gray-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-all"
                  onClick={() => {
                    setShowWhatsAppModal(true);
                    trackButtonClick('WhatsApp Icon Mobile', 'navbar', {
                      section: 'navigation_mobile'
                    });
                  }}
                  title="WhatsApp Updates"
                >
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                </button>
              )}
              <button 
                className="px-3 py-2 text-sm bg-white text-blue-600 border-2 border-blue-600 hover:bg-blue-50 rounded-lg font-medium transition-all"
                onClick={handleBookDemoClick}
              >
                Book a Demo
              </button>
            </nav>

            {/* Auth Buttons */}
            <div className="flex items-center justify-center gap-3 px-4">
              <button
                onClick={handleLogin}
                className="px-3 py-1.5 text-sm text-blue-600 font-semibold hover:text-blue-700 transition-all duration-200"
              >
                Log in
              </button>
              <button
                onClick={handleTryForFree}
                className="flex-1 sm:flex-none px-4 sm:px-5 py-1.5 sm:py-2 text-sm text-white border-2 border-blue-600 bg-blue-600 rounded-lg font-semibold hover:bg-blue-700 hover:border-blue-700 shadow-lg shadow-blue-600/30 hover:shadow-blue-600/40 transition-all duration-200 transform hover:-translate-y-0.5 whitespace-nowrap"
              >
                Sign Up for Free
              </button>
            </div>
          </div>
        </header>

        <div className="main-content">
          {/* Hero Section */}
          <motion.div 
            className="hero-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="hero-content">
              <motion.div
                key={`header-${currentIndex}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isTransitioning ? 0 : 1, y: isTransitioning ? -20 : 0 }}
                transition={{ duration: 0.4, ease: "easeInOut" }}
              >
                <Typography variant="h1" className="hero-title">
                  {renderHeader(headers[currentIndex], currentIndex)}
                </Typography>
              </motion.div>
              
              <motion.div
                key={`subheader-${currentIndex}`}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: isTransitioning ? 0 : 1, y: isTransitioning ? -20 : 0 }}
                transition={{ duration: 0.4, ease: "easeInOut", delay: 0.1 }}
              >
                <Typography variant="body1" className="hero-description">
                  {subheaders[currentIndex]}
                </Typography>
              </motion.div>
              
              <div className="cta-container">
                <Button 
                  variant="contained" 
                  size="large"
                  onClick={() => navigate('/signup')}
                  className="hero-cta"
                >
                  👉 See How It Works
                </Button>
              </div>
            </div>
            
            <div className="hero-image">
              <img 
                src={screenshots[0].src}
                alt="Yesod App"
                className="hero-image-content"
                draggable="false"
              />
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Features Section */}
          <motion.div 
            id="notifications-section"
            className="features-hero-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
          >
            <Typography variant="h2" className="features-hero-title">
              It's time to make it easy for members to engage with your community
            </Typography>
            <Typography variant="body1" className="features-hero-subtitle">
              End the digging through emails, searching through groups, and filling out long forms just to RSVP, make a donation, or pay dues.
            </Typography>

            <div className="comparison-container">
            {/* Left Side - Current Fragmented Approach */}
            <div className="fragmented-tools">
              <div className="tools-header">
                <Typography variant="h3" className="comparison-title">
                  What your members go through
                </Typography>
                <Typography variant="body2" className="comparison-subtitle">
                  Just to donate, RSVP, or check minyan times
                </Typography>
              </div>
              
              {/* Messy visualization with icons and lines */}
              <div className="messy-tools-visual">
                <svg className="connection-lines" viewBox="0 0 500 350" preserveAspectRatio="none">
                  {/* Multiple tangled curved paths */}
                  <path d="M50,50 Q150,100 250,80 T450,120" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.5"/>
                  <path d="M100,40 Q200,120 300,100 T480,90" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                  <path d="M30,100 Q120,60 220,140 T420,110" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.45"/>
                  <path d="M80,150 Q180,80 280,160 T460,140" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.4"/>
                  <path d="M40,180 Q140,220 240,180 T440,200" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.5"/>
                  <path d="M90,200 Q190,150 290,220 T470,180" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                  <path d="M60,240 Q160,190 260,250 T450,230" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.45"/>
                  <path d="M110,260 Q210,280 310,240 T480,270" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.4"/>
                  <path d="M20,280 Q130,250 230,300 T430,290" stroke="#cbd5e1" strokeWidth="4" fill="none" opacity="0.5"/>
                  <path d="M70,310 Q170,330 270,290 T460,320" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                  {/* Additional criss-cross paths */}
                  <path d="M480,60 Q380,140 280,100 T80,160" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.35"/>
                  <path d="M450,110 Q350,70 250,150 T50,120" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                  <path d="M470,180 Q370,220 270,170 T70,240" stroke="#cbd5e1" strokeWidth="3" fill="none" opacity="0.35"/>
                  <path d="M460,250 Q360,190 260,260 T60,220" stroke="#cbd5e1" strokeWidth="3.5" fill="none" opacity="0.4"/>
                </svg>
                
                <div className="tools-grid">
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#25D366">
                        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
                      </svg>
                    </div>
                    <span className="tool-name">WhatsApp</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#EA4335">
                        <path d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Gmail</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#4285F4">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Google Forms</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#0F9D58">
                        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Sheets</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#6D1ED4">
                        <path d="M20.625 8.127c-2.05 0-3.719 1.67-3.719 3.719 0 2.05 1.669 3.719 3.719 3.719 2.05 0 3.719-1.67 3.719-3.719 0-2.05-1.669-3.719-3.719-3.719zm0 6.375c-1.467 0-2.656-1.19-2.656-2.656s1.189-2.656 2.656-2.656c1.467 0 2.656 1.19 2.656 2.656s-1.189 2.656-2.656 2.656zM3.375 8.127C1.325 8.127-.344 9.796-.344 11.846c0 2.05 1.669 3.719 3.719 3.719 2.05 0 3.719-1.67 3.719-3.719 0-2.05-1.669-3.719-3.719-3.719zm0 6.375c-1.467 0-2.656-1.19-2.656-2.656S1.908 9.19 3.375 9.19c1.467 0 2.656 1.19 2.656 2.656s-1.189 2.656-2.656 2.656z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Zelle</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#003087">
                        <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944.901C5.026.382 5.474 0 5.998 0h7.46c2.57 0 4.578.543 5.69 1.81 1.01 1.15 1.304 2.42 1.012 4.287-.023.143-.047.288-.077.437-.983 5.05-4.349 6.797-8.647 6.797h-2.19c-.524 0-.968.382-1.05.9l-1.12 7.106zm14.146-14.42a3.35 3.35 0 0 0-.607-.541c-.013.076-.026.175-.041.254-.93 4.778-4.005 7.201-9.138 7.201h-2.19a.563.563 0 0 0-.556.479l-1.187 7.527h-.506l-.24 1.516a.56.56 0 0 0 .554.647h3.882c.46 0 .85-.334.922-.788.06-.26.76-4.852.76-4.852a.931.931 0 0 1 .922-.788h.58c3.76 0 6.705-1.528 7.565-5.946.36-1.847.174-3.388-.720-4.589z"/>
                      </svg>
                    </div>
                    <span className="tool-name">PayPal</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#5f6368">
                        <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 14H4V8h16v10zm-8-7c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z"/>
                        <circle cx="12" cy="14" r="2"/>
                        <path d="M4 6h16v2H4z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Your Website</span>
                  </div>
                  <div className="tool-card">
                    <div className="tool-logo-container">
                      <svg className="tool-logo" viewBox="0 0 24 24" fill="#3b82f6">
                        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 3c1.66 0 3 1.34 3 3s-1.34 3-3 3-3-1.34-3-3 1.34-3 3-3zm0 14.2c-2.5 0-4.71-1.28-6-3.22.03-1.99 4-3.08 6-3.08 1.99 0 5.97 1.09 6 3.08-1.29 1.94-3.5 3.22-6 3.22z"/>
                      </svg>
                    </div>
                    <span className="tool-name">Member Portal</span>
                  </div>
                </div>
                
                {/* Question bubbles - positioned around the edges */}
                <div className="question-bubble bubble-1">What time is Mincha?</div>
                <div className="question-bubble bubble-2">Can someone share the donation link?</div>
                <div className="question-bubble bubble-3">Where's the RSVP form?</div>
                <div className="question-bubble bubble-4">Which WhatsApp group is it in?</div>
                <div className="question-bubble bubble-5">Is this the right email?</div>
              </div>
              
              <div className="tools-issue-label">
                Messages get buried → Links get lost → Members disengage
              </div>
            </div>

            {/* Arrow */}
            <div className="arrow-divider">
              <svg className="arrow-svg" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </div>

            {/* Right Side - Yesod Solution */}
            <div className="yesod-solution">
              <div className="solution-header">
                <Typography variant="h3" className="comparison-title">
                  With Yesod
                </Typography>
                <div className="solution-subtitle">Push notifications deliver 8× more conversions than email</div>
              </div>
              
              {/* Stacked Notifications */}
              <div className="stacked-notifications">
                <div className="notification-card">
                  <div className="notif-icon">
                    <img 
                      src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png" 
                      alt="Yesod"
                    />
                  </div>
                  <div className="notif-text">
                    <div className="notif-title">Your pledge is ready to complete. Tap to finish.</div>
                    <div className="notif-subtitle">Chabad of West Tomoka • now</div>
                  </div>
                </div>

                <div className="notification-card">
                  <div className="notif-icon">
                    <img 
                      src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png" 
                      alt="Yesod"
                    />
                  </div>
                  <div className="notif-text">
                    <div className="notif-title">Upcoming: Chanukah Party 2025! Tap to RSVP</div>
                    <div className="notif-subtitle">Chabad of West Tomoka • 5m ago</div>
                  </div>
                </div>

                <div className="notification-card">
                  <div className="notif-icon">
                    <img 
                      src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png" 
                      alt="Yesod"
                    />
                  </div>
                  <div className="notif-text">
                    <div className="notif-title">Mincha starting in 10 minutes</div>
                    <div className="notif-subtitle">Chabad of West Tomoka • 15m ago</div>
                  </div>
                </div>

                <div className="notification-card">
                  <div className="notif-icon">
                    <img 
                      src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png" 
                      alt="Yesod"
                    />
                  </div>
                  <div className="notif-text">
                    <div className="notif-title">This week's newsletter</div>
                    <div className="notif-subtitle">Chabad of West Tomoka • 3h ago</div>
                  </div>
                </div>
              </div>

              <div className="tools-issue-label" style={{ marginTop: '24px', fontSize: '0.8rem' }}>
                Instant notifications → One tap to action → Instant engagement
              </div>
            </div>
          </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Pledge Automation Feature Section */}
          <motion.div 
            id="pledges-section"
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container">
              <div className="candle-video-container">
                <img 
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/Frame%2047-min.png"
                  alt="Automated Pledges"
                  className="candle-video"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    Never Chase a Pledge Again
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    Add a pledge once—Yesod handles the follow-up. Members automatically receive reminders inside the app, see their open balance, and can pay instantly. Your community stays on top of every commitment, and you stay out of the follow-up business.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Pledge Tracking Feature Section */}
          <motion.div 
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container reverse">
              <div className="candle-video-container">
                <img 
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/Frame%2046-min.png"
                  alt="Pledge Tracking"
                  className="candle-video"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    Members Pay Their Pledges Faster
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    No forms. No broken links. No emails getting lost. Members see their pledge the moment they open the app and can pay in seconds.
                    <br /><br />
                    Yesod makes giving frictionless—so you collect more, faster, with no chasing.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Candle Lighting Feature Section */}
          <motion.div 
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container">
              <div className="candle-video-container">
                <video 
                  src={screenshots[1].src}
                  className="candle-video"
                  autoPlay
                  loop
                  muted
                  playsInline
                  webkit-playsinline="true"
                  crossOrigin="anonymous"
                  preload="auto"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    Give Your Members an Experience They'll Love.
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    From animated Chanukah candle lighting to checking zmanim or donating, everything feels simple, joyful, and engaging.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Classes Feature Section */}
          <motion.div 
            id="classes-section"
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container reverse">
              <div className="candle-video-container">
                <img 
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/classes.png"
                  alt="Classes"
                  className="candle-video"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    Make It Easy to Show Up.
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    Your members see what's happening — and get a gentle reminder before a class begins.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Donations Feature Section */}
          <motion.div 
            id="donations-section"
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container">
              <div className="candle-video-container">
                <img 
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/donation.png"
                  alt="Donations"
                  className="candle-video"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    Make Giving Simple and Instant.
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    3 out of 5 people abandon donation forms. Yesod fixes that.
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description" style={{ marginTop: '12px' }}>
                    Your members can donate in seconds — no forms, no searching for links, no friction.
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description" style={{ marginTop: '12px', fontWeight: 600 }}>
                    One tap. One place. More generosity.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Community Updates Feature Section */}
          <motion.div 
            id="posts-section"
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container reverse">
              <div className="candle-video-container">
                <img 
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/updatesfeed.png"
                  alt="Community Updates"
                  className="candle-video"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    Keep Your Community in the Loop
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    Post announcements, newsletters, and updates—directly to your members' app. Fast, clear, and always seen.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Community Calendar Feature Section */}
          <motion.div 
            id="calendar-section"
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container">
              <div className="candle-video-container">
                <img 
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/calendar.png"
                  alt="Community Calendar"
                  className="candle-video"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    One Calendar. All Your Community Events.
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    See every class, event, and gathering in one simple, easy-to-use calendar. Multiple calendars, one clear view.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Prayer Times Feature Section */}
          <motion.div 
            id="prayer-section"
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container reverse">
              <div className="candle-video-container">
                <img 
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/prayer.png"
                  alt="Prayer Times"
                  className="candle-video"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    Help Your Members Prioritize What Matters.
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    When minyan times are easy to find — and reminders arrive right on time — showing up becomes natural.
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description" style={{ marginTop: '12px', fontWeight: 600 }}>
                    Simple tools. Stronger community.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Book a Demo Section */}
          <AnimatedSection 
            id="demo-section" 
            className="demo-section"
            delay={0.1}
          >
            <div className="demo-content">
              <div className="demo-header">
                <Typography variant="h2" className="demo-title">
                  See Yesod in Action
                </Typography>
                <Typography variant="body1" className="demo-subtitle">
                  Schedule a personalized 30-minute demo and discover how Yesod helps you increase donations, strengthen engagement, and offer members a simple, modern experience.
                </Typography>
              </div>
              <div className="calendly-inline-widget" data-url="https://calendly.com/yesod-demo/30min?hide_event_type_details=1&embed_domain=yesod.us&embed_type=Inline" style={{width: '100%', height: '900px', minHeight: '900px'}}></div>
            </div>
          </AnimatedSection>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* Event Details Feature Section */}
          <motion.div 
            id="events-section"
            className="candle-feature-section"
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="candle-feature-container reverse">
              <div className="candle-video-container">
                <img 
                  src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Chanukah/eventdetails.png"
                  alt="Event Details"
                  className="candle-video"
                />
              </div>
              <div className="candle-text-container">
                <div>
                  <Typography variant="h2" className="candle-feature-title">
                    Event Details & Easy Registration
                  </Typography>
                  <Typography variant="body1" className="candle-feature-description">
                    See all the info, choose your ticket, and pay directly—right in the app. Simple, fast, and secure.
                  </Typography>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Visual Breaker */}
          <div className="section-breaker">
            <div className="breaker-line"></div>
          </div>

          {/* AI Assistant Section - Hero Feature */}
          <AnimatedSection 
            id="ai-assistant-section"
            className="ai-assistant-section"
            delay={0.1}
          >
            <div className="ai-assistant-hero">
              <div className="ai-hero-badge">
                <span className="ai-badge-icon">✨</span>
                <span className="ai-badge-text">NEW</span>
              </div>
              <Typography variant="h2" className="ai-hero-title">
                Your Community, Powered by AI
              </Typography>
              <Typography variant="h4" className="ai-hero-subtitle">
                Type what you need, and Yesod AI brings it to life in your app—classes, events, prayer times, and more. No tech skills required.
              </Typography>
              <div className="ai-hero-visual">
                <div className="ai-admin-mockup-large">
                  <div className="admin-modal-demo-large">
                    <div className="modal-close-btn">×</div>
                    
                    <div className="modal-icon-large">
                      <img 
                        src="https://xvhellooihpelwifxuik.supabase.co/storage/v1/object/public/assets/Yesod%20Logo%204K.png"
                        alt="Yesod Logo"
                        className="yesod-logo-image"
                      />
                    </div>
                    
                    <div className="modal-header-text-large">
                      <h2>What would you like me to create?</h2>
                    </div>
                    
                    <div className="modal-input-section-large">
                      <textarea 
                        className="ai-modal-input-large"
                        placeholder="Create a Torah class every Sunday at 7 PM with Rabbi Cohen…"
                        rows="3"
                      />
                    </div>
                    
                    <div className="ai-button-section-large">
                      <button 
                        className="create-with-ai-btn-large"
                        onClick={() => navigate('/signup')}
                      >
                        <span className="btn-sparkle">✨</span>
                        Create Instantly
                      </button>
                      <p className="ai-microcopy">
                        Everything appears directly in your app—fast, simple, and effortless.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </AnimatedSection>
        </div>

        {/* Visual Breaker */}
        <div className="section-breaker">
          <div className="breaker-line"></div>
        </div>

        {/* FAQ Section */}
        <AnimatedSection 
          id="faq-section" 
          className="faq-section"
          delay={0.8}
        >
          <div className="faq-content">
            <Typography variant="h2" className="faq-title">
              Frequently Asked Questions
            </Typography>
            <div className="faq-list">
              <FAQItem 
                question="Will my members see this immediately?"
                answer="Not unless you invite them. You're in control of when and how you share it."
              />
              <FAQItem 
                question="Can I try it without committing?"
                answer="Absolutely. You can explore everything before inviting anyone. It's risk-free."
              />
              <FAQItem 
                question="Is there a limit to how many members can join my community?"
                answer="Nope — there's no limit. You can have as many members as you want."
              />
              <FAQItem 
                question="How much does Yesod cost?"
                answer={
                  <Typography variant="body1" className="answer-text">
                    Yesod is completely free to use. For premium features and pricing,{' '}
                    <Button 
                      variant="text" 
                      sx={{ 
                        textTransform: 'none', 
                        color: '#3b82f6',
                        textDecoration: 'underline',
                        padding: 0,
                        minWidth: 'auto',
                        '&:hover': {
                          textDecoration: 'underline',
                          backgroundColor: 'transparent'
                        }
                      }}
                      onClick={() => {
                        navigate('/pricing');
                        window.scrollTo(0, 0);
                      }}
                    >
                      visit our pricing page
                    </Button>
                    .
                  </Typography>
                }
              />
              <FAQItem 
                question="Does it cost anything for members?"
                answer="Nope. It's free for your members, too."
              />
              <FAQItem 
                question="Does Yesod integrate with ChabadOne/Salesforce?"
                answer="Yesod can integrate with Salesforce for Premium users. Even with a free account, you can always easily export your data (pledges, payments, donations, registrations) and upload it to any system you use."
              />
              <FAQItem 
                question="Can more than one person manage the community?"
                answer="Currently, each community can only have one admin."
              />
              <FAQItem 
                question="How do I get in touch with your support?"
                answer="Email us at shalom@yesod.us"
              />
            </div>
          </div>
        </AnimatedSection>

        {/* WhatsApp Updates Section - Clean & Simple */}
        {config.FEATURE_FLAGS?.WHATSAPP_SECTION && (
          <AnimatedSection 
            className="whatsapp-updates-section"
            delay={0.2}
          >
            <div className="whatsapp-content">
              <div className="whatsapp-icon-wrapper">
                <svg className="whatsapp-icon" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
              </div>
              <Typography variant="body1" className="whatsapp-intro">
                Due to growing interest, we're opening a small WhatsApp group for shluchim who are either already using Yesod or actively exploring it.
              </Typography>
              <div className="whatsapp-quiet-space">
                <Typography variant="body2" className="whatsapp-quiet-label">
                  It's a quiet space to:
                </Typography>
                <div className="whatsapp-benefits">
                  <div className="whatsapp-benefit">• See feature updates</div>
                  <div className="whatsapp-benefit">• Watch how Yesod is being used in real communities</div>
                  <div className="whatsapp-benefit">• Share questions or ideas</div>
                </div>
              </div>
              <Typography variant="body2" className="whatsapp-disclaimer">
                No noise. No marketing blasts. You can leave anytime.
              </Typography>
              <Button 
                className="whatsapp-cta-btn"
                onClick={() => {
                  setShowWhatsAppModal(true);
                  trackButtonClick('Request Access', 'landing_page_mid', {
                    section: 'whatsapp_updates'
                  });
                }}
              >
                Request Access
              </Button>
            </div>
          </AnimatedSection>
        )}

        {/* Footer Section */}
        <footer className="main-footer">
          <div className="footer-content">
              <div className="footer-info">
                <Typography variant="body2" className="footer-text">
                  © 2026 Yesod. All rights reserved.
                </Typography>
                <Typography variant="body2" className="footer-support">
                  Support: <a href="mailto:shalom@yesod.us" className="support-email">shalom@yesod.us</a>
                </Typography>
              </div>
              <div className="footer-legal">
                <Link to="/terms-of-service" target="_blank" className="legal-footer-link">Terms of Service</Link>
                <span className="legal-separator">•</span>
                <Link to="/privacy-policy" target="_blank" className="legal-footer-link">Privacy Policy</Link>
                {config.FEATURE_FLAGS?.WHATSAPP_SECTION && (
                  <>
                    <span className="legal-separator">•</span>
                    <button 
                      onClick={() => {
                        setShowWhatsAppModal(true);
                        trackButtonClick('WhatsApp Updates', 'footer', {
                          section: 'footer_cta'
                        });
                      }}
                      className="footer-whatsapp-link"
                    >
                      WhatsApp Updates
                    </button>
                  </>
                )}
              </div>
            </div>
        </footer>

        {/* Expanded Image Modal */}
        {expandedImage && (
          <div 
            className="image-modal-overlay"
            onClick={() => setExpandedImage(null)}
          >
            <div 
              className="image-modal-content" 
              onClick={(e) => e.stopPropagation()}
              onTouchStart={handleModalTouchStart}
              onTouchMove={handleModalTouchMove}
              onTouchEnd={handleModalTouchEnd}
            >
              <button 
                className="image-modal-close"
                onClick={() => setExpandedImage(null)}
                aria-label="Close expanded image"
              >
                ×
              </button>
              
              {/* Navigation buttons */}
              {appScreenshots.length > 1 && (
                <>
                  <button 
                    className="image-modal-nav prev"
                    onClick={handlePrevImage}
                    aria-label="Previous image"
                  >
                    ←
                  </button>
                  <button 
                    className="image-modal-nav next"
                    onClick={handleNextImage}
                    aria-label="Next image"
                  >
                    →
                  </button>
                </>
              )}
              
              <img 
                src={expandedImage}
                alt="Expanded Yesod App Screenshot"
                className="expanded-image"
              />
              
              {/* Image counter */}
              {appScreenshots.length > 1 && (
                <div className="image-modal-counter">
                  {expandedImageIndex + 1} / {appScreenshots.length}
                  {isMobile && (
                    <div className="swipe-hint">← Swipe to navigate →</div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
        
        {/* WhatsApp Request Modal */}
        <WhatsAppRequestModal 
          isOpen={showWhatsAppModal}
          onClose={() => setShowWhatsAppModal(false)}
        />
    </div>
  );
};

export default MainLanding;
